/**
 * Record Milk Production - Create new milk production record
 * Updated to use shed-based tracking instead of individual cattle tracking
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Milk, AlertCircle } from 'lucide-react';
import { milkProductionApi, shedApi, type MilkRecord, type Shed } from '../../../services/gaushala/api';
import { getLoggedInUserGaushalaId } from '../../../utils/auth';

export default function RecordMilkProduction() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sheds, setSheds] = useState<Shed[]>([]);
  const [loadingSheds, setLoadingSheds] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validation rules for field lengths
  const VALIDATION_RULES = {
    notes: { max: 500 },
  };

  const [formData, setFormData] = useState<Partial<MilkRecord>>({
    shedNumber: '',
    gaushalaId: 0,
    milkQuantity: 0,
    fatPercentage: 0,
    snf: 0,
    notes: '',
    status: 'RECORDED',
  });

  useEffect(() => {
    // Get gaushalaId from JWT, or use fallback for testing
    const gaushalaId = getLoggedInUserGaushalaId() || 15;
    setFormData((prev) => ({ ...prev, gaushalaId }));
    loadSheds();
  }, []);

  const loadSheds = async () => {
    setLoadingSheds(true);
    try {
      // Load list of sheds
      const response = await shedApi.getAllSheds(0, 100);
      if (response.success && response.data) {
        // Filter only active sheds
        const activeSheds = response.data.content.filter(
          (shed) => shed.status === 'ACTIVE'
        );
        setSheds(activeSheds);
      }
    } catch (error) {
      console.error('Error loading sheds:', error);
    } finally {
      setLoadingSheds(false);
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.shedNumber || formData.shedNumber === '') {
      newErrors.shedNumber = 'Please select a shed';
    }

    if (!formData.milkQuantity || formData.milkQuantity <= 0) {
      newErrors.milkQuantity = 'Milk quantity must be greater than 0';
    } else if (formData.milkQuantity > 1000) {
      newErrors.milkQuantity = 'Milk quantity seems too high (max 1000 liters)';
    }

    if (formData.fatPercentage && formData.fatPercentage < 0) {
      newErrors.fatPercentage = 'Fat percentage cannot be negative';
    } else if (formData.fatPercentage && formData.fatPercentage > 100) {
      newErrors.fatPercentage = 'Fat percentage cannot exceed 100%';
    }

    if (formData.snf && formData.snf < 0) {
      newErrors.snf = 'SNF cannot be negative';
    } else if (formData.snf && formData.snf > 100) {
      newErrors.snf = 'SNF cannot exceed 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await milkProductionApi.createMilkRecord(formData as MilkRecord);
      if (response.success) {
        alert('Milk production record created successfully!');
        navigate('/gaushala/production');
      } else {
        setErrors({ submit: response.error || 'Failed to create record' });
      }
    } catch (error) {
      console.error('Error creating record:', error);
      setErrors({ submit: 'Error creating record' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof MilkRecord, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate('/gaushala/production')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        Back to Records
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-full bg-blue-100 p-3">
          <Milk className="text-blue-600" size={24} />
        </div>
        <h1 className="text-2xl font-bold">Record Milk Production</h1>
      </div>

      {/* Error Alert */}
      {errors.submit && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{errors.submit}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Shed <span className="text-red-500">*</span>
            </label>
            {loadingSheds ? (
              <div className="text-sm text-gray-500">Loading sheds...</div>
            ) : (
              <>
                <select
                  value={formData.shedNumber}
                  onChange={(e) => handleChange('shedNumber', e.target.value)}
                  className={`w-full rounded-lg border px-4 py-2 ${errors.shedNumber ? 'border-red-500' : ''}`}
                  required
                >
                  <option value="">Select a shed</option>
                  {sheds.map((shed) => (
                    <option key={shed.id} value={shed.shedNumber}>
                      {shed.shedNumber} - {shed.shedName} (Capacity: {shed.currentOccupancy}/{shed.capacity})
                    </option>
                  ))}
                </select>
                {errors.shedNumber && (
                  <p className="text-sm text-red-500 mt-1">{errors.shedNumber}</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Milk Quantity (Liters) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.milkQuantity}
              onChange={(e) => handleChange('milkQuantity', parseFloat(e.target.value) || 0)}
              className={`w-full rounded-lg border px-4 py-2 ${errors.milkQuantity ? 'border-red-500' : ''}`}
              placeholder="Total liters collected from this shed"
              required
            />
            {errors.milkQuantity && (
              <p className="text-sm text-red-500 mt-1">{errors.milkQuantity}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fat Percentage
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={formData.fatPercentage}
              onChange={(e) => handleChange('fatPercentage', parseFloat(e.target.value) || 0)}
              className={`w-full rounded-lg border px-4 py-2 ${errors.fatPercentage ? 'border-red-500' : ''}`}
              placeholder="e.g., 4"
            />
            {errors.fatPercentage && (
              <p className="text-sm text-red-500 mt-1">{errors.fatPercentage}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SNF (Solids Not Fat)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={formData.snf}
              onChange={(e) => handleChange('snf', parseFloat(e.target.value) || 0)}
              className={`w-full rounded-lg border px-4 py-2 ${errors.snf ? 'border-red-500' : ''}`}
              placeholder="e.g., 8"
            />
            {errors.snf && (
              <p className="text-sm text-red-500 mt-1">{errors.snf}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value)}
            className="w-full rounded-lg border px-4 py-2"
          >
            <option value="RECORDED">Recorded</option>
            <option value="VERIFIED">Verified</option>
            <option value="PROCESSED">Processed</option>
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <span className="text-xs text-gray-500">({formData.notes.length}/{VALIDATION_RULES.notes.max})</span>
          </div>
          <textarea
            value={formData.notes}
            onChange={(e) => {
              if (e.target.value.length <= VALIDATION_RULES.notes.max) {
                handleChange('notes', e.target.value);
              }
            }}
            maxLength={VALIDATION_RULES.notes.max}
            className="w-full rounded-lg border px-4 py-2"
            rows={3}
            placeholder="Additional notes about this production record (e.g., morning/evening collection, quality observations)..."
          ></textarea>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              'Saving...'
            ) : (
              <>
                <Save size={20} />
                Save Record
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/gaushala/production')}
            className="px-6 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
